import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { 
  Menu, 
  X, 
  ChevronLeft, 
  ChevronRight,
  Home,
  Search,
  Bookmark,
  Settings,
  Eye,
  EyeOff,
  Type,
  Palette,
  Volume2,
  VolumeX,
  Download,
  Wifi,
  WifiOff,
  Smartphone,
  Tablet,
  Monitor
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

// Mobile & Accessibility Optimized Layout for The Academy Platform
// Implements responsive design, offline capabilities, and accessibility features

// Hook for detecting device type and screen size
export const useDeviceDetection = () => {
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('landscape');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setDeviceType('mobile');
      } else if (width < 1024) {
        setDeviceType('tablet');
      } else {
        setDeviceType('desktop');
      }
      
      setOrientation(window.innerHeight > window.innerWidth ? 'portrait' : 'landscape');
    };

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    checkDevice();
    window.addEventListener('resize', checkDevice);
    window.addEventListener('orientationchange', checkDevice);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { deviceType, orientation, isOnline };
};

// Accessibility preferences context
interface AccessibilityPreferences {
  highContrast: boolean;
  largeText: boolean;
  reducedMotion: boolean;
  screenReader: boolean;
  audioDescription: boolean;
  fontSize: 'small' | 'medium' | 'large' | 'extra-large';
  colorScheme: 'light' | 'dark' | 'auto';
}

const defaultPreferences: AccessibilityPreferences = {
  highContrast: false,
  largeText: false,
  reducedMotion: false,
  screenReader: false,
  audioDescription: false,
  fontSize: 'medium',
  colorScheme: 'auto'
};

interface AccessibilityContextType {
  preferences: AccessibilityPreferences;
  updatePreference: <K extends keyof AccessibilityPreferences>(
    key: K,
    value: AccessibilityPreferences[K]
  ) => void;
  resetPreferences: () => void;
}

const AccessibilityContext = React.createContext<AccessibilityContextType | null>(null);

export const useAccessibility = () => {
  const context = React.useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

// Accessibility Provider Component
export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<AccessibilityPreferences>(() => {
    const saved = localStorage.getItem('academy-accessibility-preferences');
    return saved ? JSON.parse(saved) : defaultPreferences;
  });

  const updatePreference = <K extends keyof AccessibilityPreferences>(
    key: K,
    value: AccessibilityPreferences[K]
  ) => {
    setPreferences(prev => {
      const updated = { ...prev, [key]: value };
      localStorage.setItem('academy-accessibility-preferences', JSON.stringify(updated));
      return updated;
    });
  };

  const resetPreferences = () => {
    setPreferences(defaultPreferences);
    localStorage.removeItem('academy-accessibility-preferences');
  };

  // Apply preferences to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Font size
    const fontSizeMap = {
      small: '14px',
      medium: '16px',
      large: '18px',
      'extra-large': '20px'
    };
    root.style.fontSize = fontSizeMap[preferences.fontSize];

    // Color scheme
    if (preferences.colorScheme !== 'auto') {
      root.setAttribute('data-theme', preferences.colorScheme);
    }

    // High contrast
    if (preferences.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Reduced motion
    if (preferences.reducedMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }

    // Large text
    if (preferences.largeText) {
      root.classList.add('large-text');
    } else {
      root.classList.remove('large-text');
    }
  }, [preferences]);

  return (
    <AccessibilityContext.Provider value={{ preferences, updatePreference, resetPreferences }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

// Mobile Navigation Component
interface MobileNavigationProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  navigationItems: Array<{
    id: string;
    label: string;
    icon: React.ComponentType<any>;
    href: string;
    badge?: string;
  }>;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({
  isOpen,
  onOpenChange,
  navigationItems
}) => {
  const { preferences } = useAccessibility();
  
  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent 
        side="left" 
        className={cn(
          'w-80 p-0',
          preferences.highContrast && 'border-white'
        )}
        aria-label="Main navigation menu"
      >
        <SheetHeader className="p-4 border-b">
          <SheetTitle className="text-left">Navigation Menu</SheetTitle>
        </SheetHeader>
        
        <ScrollArea className="flex-1 px-4">
          <nav className="space-y-2 py-4" role="navigation">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.id}
                  variant="ghost"
                  className={cn(
                    'w-full justify-start h-auto p-4',
                    'focus:ring-2 focus:ring-primary focus:ring-offset-2'
                  )}
                  onClick={() => onOpenChange(false)}
                  asChild
                >
                  <a href={item.href}>
                    <Icon className="h-5 w-5 mr-3" aria-hidden="true" />
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </a>
                </Button>
              );
            })}
          </nav>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

// Accessibility Settings Panel
export const AccessibilitySettings: React.FC<{ className?: string }> = ({ className }) => {
  const { preferences, updatePreference, resetPreferences } = useAccessibility();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('flex items-center gap-2', className)}
          aria-label="Open accessibility settings"
        >
          <Eye className="h-4 w-4" />
          <span className="hidden sm:inline">Accessibility</span>
        </Button>
      </SheetTrigger>
      
      <SheetContent side="right" className="w-80" aria-label="Accessibility settings panel">
        <SheetHeader>
          <SheetTitle>Accessibility Settings</SheetTitle>
        </SheetHeader>
        
        <ScrollArea className="flex-1 mt-6">
          <div className="space-y-6">
            {/* Visual Preferences */}
            <div className="space-y-4">
              <h3 className="font-medium">Visual Preferences</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label htmlFor="high-contrast" className="text-sm font-medium">
                    High Contrast
                  </label>
                  <Button
                    id="high-contrast"
                    variant={preferences.highContrast ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updatePreference('highContrast', !preferences.highContrast)}
                    aria-pressed={preferences.highContrast}
                  >
                    {preferences.highContrast ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                </div>

                <div className="space-y-2">
                  <label htmlFor="font-size" className="text-sm font-medium">
                    Font Size
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['small', 'medium', 'large', 'extra-large'] as const).map((size) => (
                      <Button
                        key={size}
                        variant={preferences.fontSize === size ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updatePreference('fontSize', size)}
                        className="text-xs"
                      >
                        {size === 'extra-large' ? 'XL' : size.charAt(0).toUpperCase()}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="color-scheme" className="text-sm font-medium">
                    Color Scheme
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {(['light', 'dark', 'auto'] as const).map((scheme) => (
                      <Button
                        key={scheme}
                        variant={preferences.colorScheme === scheme ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updatePreference('colorScheme', scheme)}
                        className="text-xs"
                      >
                        <Palette className="h-3 w-3 mr-1" />
                        {scheme}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Motion & Animation */}
            <div className="space-y-4">
              <h3 className="font-medium">Motion & Animation</h3>
              
              <div className="flex items-center justify-between">
                <label htmlFor="reduced-motion" className="text-sm font-medium">
                  Reduce Motion
                </label>
                <Button
                  id="reduced-motion"
                  variant={preferences.reducedMotion ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => updatePreference('reducedMotion', !preferences.reducedMotion)}
                >
                  {preferences.reducedMotion ? 'On' : 'Off'}
                </Button>
              </div>
            </div>

            {/* Audio Preferences */}
            <div className="space-y-4">
              <h3 className="font-medium">Audio Preferences</h3>
              
              <div className="flex items-center justify-between">
                <label htmlFor="audio-description" className="text-sm font-medium">
                  Audio Descriptions
                </label>
                <Button
                  id="audio-description"
                  variant={preferences.audioDescription ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => updatePreference('audioDescription', !preferences.audioDescription)}
                >
                  {preferences.audioDescription ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Screen Reader */}
            <div className="space-y-4">
              <h3 className="font-medium">Screen Reader</h3>
              
              <div className="flex items-center justify-between">
                <label htmlFor="screen-reader" className="text-sm font-medium">
                  Screen Reader Mode
                </label>
                <Button
                  id="screen-reader"
                  variant={preferences.screenReader ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => updatePreference('screenReader', !preferences.screenReader)}
                >
                  {preferences.screenReader ? 'On' : 'Off'}
                </Button>
              </div>
            </div>

            {/* Reset */}
            <div className="pt-4 border-t">
              <Button
                variant="outline"
                onClick={resetPreferences}
                className="w-full"
              >
                Reset to Defaults
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

// Mobile-Optimized Content Layout
interface MobileContentLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  sidebar?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

export const MobileContentLayout: React.FC<MobileContentLayoutProps> = ({
  title,
  subtitle,
  children,
  sidebar,
  actions,
  className
}) => {
  const { deviceType, orientation } = useDeviceDetection();
  const { preferences } = useAccessibility();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isMobile = deviceType === 'mobile';
  const isTablet = deviceType === 'tablet';

  return (
    <div className={cn(
      'min-h-screen bg-background',
      preferences.highContrast && 'bg-black text-white',
      className
    )}>
      {/* Mobile Header */}
      {isMobile && (
        <header className="sticky top-0 z-40 bg-background border-b px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {sidebar && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSidebarOpen(true)}
                  aria-label="Open sidebar menu"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              )}
              <div>
                <h1 className={cn(
                  'font-semibold',
                  preferences.largeText ? 'text-lg' : 'text-base'
                )}>
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-xs text-black">{subtitle}</p>
                )}
              </div>
            </div>
            
            {actions && (
              <div className="flex items-center gap-1">
                {actions}
              </div>
            )}
          </div>
        </header>
      )}

      <div className="flex">
        {/* Sidebar */}
        {sidebar && (
          <>
            {isMobile ? (
              <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetContent side="left" className="w-80 p-0">
                  <ScrollArea className="h-full p-4">
                    {sidebar}
                  </ScrollArea>
                </SheetContent>
              </Sheet>
            ) : (
              <aside className={cn(
                'w-64 border-r bg-muted/30 p-4',
                isTablet && orientation === 'portrait' && 'w-56'
              )}>
                <ScrollArea className="h-full">
                  {sidebar}
                </ScrollArea>
              </aside>
            )}
          </>
        )}

        {/* Main Content */}
        <main 
          className={cn(
            'flex-1 p-4',
            isMobile && 'p-3',
            !isMobile && !sidebar && 'max-w-none'
          )}
          role="main"
        >
          {/* Desktop Header */}
          {!isMobile && (
            <header className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className={cn(
                    'text-2xl font-bold',
                    preferences.largeText && 'text-3xl'
                  )}>
                    {title}
                  </h1>
                  {subtitle && (
                    <p className="text-black mt-1">{subtitle}</p>
                  )}
                </div>
                {actions && (
                  <div className="flex items-center gap-2">
                    {actions}
                  </div>
                )}
              </div>
            </header>
          )}

          {/* Content */}
          <div className={cn(
            'space-y-4',
            isMobile && 'space-y-3'
          )}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

// Offline Reading Component
export const OfflineReadingManager: React.FC = () => {
  const { isOnline } = useDeviceDetection();
  const [offlineContent, setOfflineContent] = useState<string[]>([]);
  const [downloadQueue, setDownloadQueue] = useState<string[]>([]);

  const downloadForOffline = async (contentId: string) => {
    setDownloadQueue(prev => [...prev, contentId]);
    
    try {
      // Simulate offline caching
      await new Promise(resolve => setTimeout(resolve, 1000));
      setOfflineContent(prev => [...prev, contentId]);
      setDownloadQueue(prev => prev.filter(id => id !== contentId));
    } catch (error) {
      console.error('Failed to cache content:', error);
      setDownloadQueue(prev => prev.filter(id => id !== contentId));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isOnline ? <Wifi className="h-5 w-5" /> : <WifiOff className="h-5 w-5" />}
          Offline Reading
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm">Connection Status:</span>
            <span className={cn(
              'text-sm font-medium',
              isOnline ? 'text-green-600' : 'text-red-600'
            )}>
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm">Downloaded Content:</span>
            <span className="text-sm font-medium">{offlineContent.length} items</span>
          </div>

          {downloadQueue.length > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-sm">Downloading:</span>
              <span className="text-sm font-medium">{downloadQueue.length} items</span>
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadForOffline('sample-content')}
            disabled={downloadQueue.length > 0}
            className="w-full"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Current Page
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Device-specific optimizations hook
export const useDeviceOptimizations = () => {
  const { deviceType, orientation } = useDeviceDetection();
  const { preferences } = useAccessibility();

  const getOptimalLayout = () => {
    if (deviceType === 'mobile') {
      return {
        columns: 1,
        spacing: 'compact',
        fontSize: 'small',
        touchTargetSize: 44
      };
    } else if (deviceType === 'tablet') {
      return {
        columns: orientation === 'portrait' ? 1 : 2,
        spacing: 'comfortable',
        fontSize: 'medium',
        touchTargetSize: 48
      };
    } else {
      return {
        columns: 3,
        spacing: 'comfortable',
        fontSize: 'medium',
        touchTargetSize: 40
      };
    }
  };

  const layout = getOptimalLayout();

  return {
    ...layout,
    deviceType,
    orientation,
    preferences,
    isTouchDevice: deviceType !== 'desktop'
  };
};
